import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import type {
  Company,
  CompanyProjectSummary,
  CompanyRollup,
  CompanyViewer,
} from '../types/api';

export const companiesKeys = {
  all: ['companies'] as const,
  list: (q: ListFilters) => [...companiesKeys.all, 'list', q] as const,
  byId: (id: string) => [...companiesKeys.all, 'byId', id] as const,
  rollup: (id: string) => [...companiesKeys.all, 'rollup', id] as const,
  viewers: (id: string) => [...companiesKeys.all, 'viewers', id] as const,
  projects: (id: string) => [...companiesKeys.all, 'projects', id] as const,
};

export interface ListFilters {
  q?: string | undefined;
  include_deleted?: boolean | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

export function useCompaniesList(filters: ListFilters = {}) {
  return useQuery({
    queryKey: companiesKeys.list(filters),
    queryFn: () => {
      const query: Record<string, string | number | boolean | undefined> = {
        q: filters.q,
        include_deleted: filters.include_deleted,
        limit: filters.limit,
        offset: filters.offset,
      };
      return api.get<{ companies: Company[]; total: number }>('/admin/companies', query);
    },
  });
}

export function useCompany(id: string | null) {
  return useQuery({
    queryKey: id ? companiesKeys.byId(id) : ['companies', 'none'],
    enabled: !!id,
    queryFn: async () => {
      const r = await api.get<{ company: Company }>(`/admin/companies/${id}`);
      return r.company;
    },
  });
}

export function useCompanyRollup(id: string | null) {
  return useQuery({
    queryKey: id ? companiesKeys.rollup(id) : ['companies', 'rollup', 'none'],
    enabled: !!id,
    queryFn: async () => {
      const r = await api.get<{ company_id: string; rollup: CompanyRollup }>(
        `/admin/companies/${id}/rollup`,
      );
      return r.rollup;
    },
  });
}

export function useCompanyProjects(id: string | null) {
  return useQuery({
    queryKey: id ? companiesKeys.projects(id) : ['companies', 'projects', 'none'],
    enabled: !!id,
    queryFn: async () => {
      const r = await api.get<{ projects: CompanyProjectSummary[] }>(
        `/admin/companies/${id}/projects`,
      );
      return r.projects;
    },
  });
}

export function useCompanyViewers(id: string | null) {
  return useQuery({
    queryKey: id ? companiesKeys.viewers(id) : ['companies', 'viewers', 'none'],
    enabled: !!id,
    queryFn: async () => {
      const r = await api.get<{ viewers: CompanyViewer[] }>(`/admin/companies/${id}/viewers`);
      return r.viewers;
    },
  });
}

export interface CompanyInput {
  name: string;
  slug?: string | undefined;
  description?: string | null | undefined;
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CompanyInput) =>
      api.post<{ company: Company }>('/admin/companies', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: companiesKeys.all }),
  });
}

export function useUpdateCompany(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<CompanyInput>) =>
      api.patch<{ company: Company }>(`/admin/companies/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: companiesKeys.all }),
  });
}

export function useDeleteCompany() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/companies/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: companiesKeys.all }),
  });
}

export function useGrantCompanyViewer(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.post(`/admin/companies/${companyId}/viewers`, { user_id: userId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: companiesKeys.viewers(companyId) }),
  });
}

export function useRevokeCompanyViewer(companyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.delete(`/admin/companies/${companyId}/viewers/${userId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: companiesKeys.viewers(companyId) }),
  });
}

export function useSetProjectCompany(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (companyId: string | null) =>
      api.patch(`/admin/projects/${projectId}/company`, { company_id: companyId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: companiesKeys.all }),
  });
}
